import { useState, useEffect } from "react";
import { Navigate, Outlet, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import NotFoundPage from "../app/pages/ErrorPage/404.tsx";
import TrialExpired from "../features/FreeTrial/TrialExpired.tsx";
import { useAppSelector } from "../redux/store.ts";
import { CreditContext } from "../features/User/type.ts";
import Modal from "../common/Modal/Modal.tsx";
import { WrongPathCategory } from "../app/pages/ErrorPage/constants.ts";
import { logger } from "../utils/consoleLogger.ts";
import { BusinessModels } from "../features/User/constants.ts";
import createSourcingAccessQuery from "../features/Billings/Wallet/api/getSourcingAccess.ts";
import SourcingPlanModal from "../features/Billings/SourcingPlan/SourcingPlanModal.tsx";

export const AccountManagerAccessOnly = ({ children }) => {
  const { user } = useAppSelector((state) => state.user);
  if (!user?.is_edit_admin) {
    logger.warn("Access denied: admin-only route", {
      email: user?.email,
      path: window.location.pathname,
    });
  }
  return user?.is_edit_admin ? (
    children
  ) : (
    <NotFoundPage type={WrongPathCategory.ADMIN_ACCESS_ONLY} />
  );
};

export const PremiumPages = ({ isPremium }) => {
  return isPremium ? (
    <Outlet />
  ) : (
    <NotFoundPage type={WrongPathCategory.PREMIUM_ACCESS_ONLY} />
  );
};

export const CreditBasedAccess = () => {
  const { slug } = useParams();
  const { user } = useAppSelector((state) => state.user);
  const userActions: CreditContext = {
    isExpired:
      user?.business_model === BusinessModels.Credit &&
      !user?.has_active_credits,
    canCreateNewRole:
      user?.business_model === BusinessModels.License ||
      (user?.has_active_credits && user?.has_unused_credits),
  };

  return userActions.isExpired && !user?.is_edit_admin ? (
    <>
      <Modal
        isOpen={true}
        onClose={null}
        title=""
        className="w-full bg-gray-700 p-0 md:w-[60vw]"
        closeOnBackdrop={false}
        hideCloseBtn
      >
        <TrialExpired slug={slug} />
      </Modal>
      <Outlet context={userActions} />
    </>
  ) : (
    <Outlet context={userActions} />
  );
};

export const PrivatePages = ({ isAuthenticated }) => {
  return isAuthenticated ? (
    <Outlet />
  ) : (
    <NotFoundPage type={WrongPathCategory.AUTH_ACCESS_ONLY} />
  );
};

export const RequireAiTalentDiscovery = ({ children }) => {
  const { slug } = useParams();
  const { user } = useAppSelector((state) => state.user);
  const [dismissedBuyModal, setDismissedBuyModal] = useState(false);
  const [dismissedPlanModal, setDismissedPlanModal] = useState(false);
  const [trialNudgeReady, setTrialNudgeReady] = useState(false);

  const { data: access, isLoading } = useQuery({
    ...createSourcingAccessQuery(slug!),
    enabled: !!user?.enable_ai_talent_discovery,
  });

  const isTrialEndingSoon =
    !isLoading &&
    access?.status === "trial" &&
    (access?.days_remaining ?? 999) <= 3;

  useEffect(() => {
    if (!isTrialEndingSoon) return;
    const timer = setTimeout(() => setTrialNudgeReady(true), 3000);
    return () => clearTimeout(timer);
  }, [isTrialEndingSoon]);

  // Fast-path: flag is false → not a sourcing customer at all
  if (!user?.enable_ai_talent_discovery) {
    return <Navigate to={`/${slug}`} replace />;
  }

  // While checking subscription status, render children optimistically (no flash)
  if (isLoading) return children;

  const blockedStatuses = ["expired", "no_plan"];
  const isBlocked = access && blockedStatuses.includes(access.status);

  const isTrialExpired = access?.status === "trial" && (access?.days_remaining ?? 0) === 0;

  // no_plan = never had access (new user) → redirect away
  // trial + days=0 = trial ended → hard block handled by SourcingExpiredNudge
  if (access?.status === "no_plan" || isTrialExpired) return <Navigate to={`/${slug}`} replace />;
  if (isBlocked) return <>{children}</>;

  const isOutOfCredits =
    access?.status === "active" && (access?.credits_remaining ?? 1) === 0;

  return (
    <>
      {children}
      {/* Active plan but 0 credits → buy credits nudge (closeable) */}
      {isOutOfCredits && !dismissedBuyModal && !user?.is_edit_admin && (
        <SourcingPlanModal
          slug={slug!}
          mode="buy-credits"
          onClose={() => setDismissedBuyModal(true)}
          onSuccess={() => setDismissedBuyModal(true)}
        />
      )}
      {/* Trial ending in ≤3 days → subscribe nudge after 3s (closeable) */}
      {isTrialEndingSoon && trialNudgeReady && !dismissedPlanModal && !user?.is_edit_admin && (
        <SourcingPlanModal
          slug={slug!}
          onClose={() => setDismissedPlanModal(true)}
          onSuccess={() => setDismissedPlanModal(true)}
        />
      )}
    </>
  );
};
